import React, { useMemo } from 'react';

export default function CustomSalesPurchaseChart({ apiResponse }) {
    const padding = 40;
    const chartWidth = 600;
    const chartHeight = 300;
    const graphWidth = chartWidth - padding * 2;
    const graphHeight = chartHeight - padding * 2;

    const salesData = apiResponse?.sales?.data || [];
    const purchaseData = apiResponse?.purchases?.data || [];
    const days = apiResponse?.sales?.days || [];

    const maxVal = useMemo(() => {
        if (salesData.length === 0 && purchaseData.length === 0) return 100;
        const combinedMax = Math.max(...salesData, ...purchaseData, 100);
        return Math.ceil(combinedMax / 100) * 100;
    }, [salesData, purchaseData]);

    const pointsCount = days.length;
    const getX = (index) => padding + (index * (graphWidth / (pointsCount - 1 || 1)));
    const getY = (value) => padding + graphHeight - (value / maxVal) * graphHeight;

    const salesPath = useMemo(() =>
        salesData.map((val, idx) => `${getX(idx)},${getY(val)}`).join(' '),
        [salesData, maxVal]
    );

    const purchasePath = useMemo(() =>
        purchaseData.map((val, idx) => `${getX(idx)},${getY(val)}`).join(' '),
        [purchaseData, maxVal]
    );

    if (days.length === 0) {
        return <div className="text-muted p-3">No data available</div>;
    }

    return (
        // Bootstrap Card container wrapper
        <div className="card p-3 shadow-sm border-light w-100" style={{ maxWidth: '650px' }}>

            {/* Bootstrap flex utilities for the Legend */}
            <div className="d-flex gap-3 mb-3 justify-content-end small fw-medium text-secondary">
                <div className="d-flex align-items-center gap-1">
                    <span className="d-inline-block rounded-circle bg-primary" style={{ width: '12px', height: '12px' }} />
                    <span>Sales</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                    <span className="d-inline-block rounded-circle bg-danger" style={{ width: '12px', height: '12px' }} />
                    <span>Purchases</span>
                </div>
            </div>

            {/* SVG canvas with responsive sizing helper class */}
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="img-fluid overflow-visible">
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                    const yPos = padding + graphHeight * ratio;
                    const labelValue = Math.round(maxVal * (1 - ratio));
                    return (
                        <g key={i} style={{ opacity: 0.25 }}>
                            <line x1={padding} y1={yPos} x2={chartWidth - padding} y2={yPos} stroke="#6c757d" strokeWidth="1" strokeDasharray="4 4" />
                            {/* Text targets Bootstrap basic variable system families */}
                            <text x={padding - 8} y={yPos + 4} textAnchor="end" fill="#212529" fontSize="10" style={{ opacity: 1, fontFamily: 'sans-serif' }}>{labelValue}</text>
                        </g>
                    );
                })}

                {/* X Axis Base Line */}
                <line x1={padding} y1={chartHeight - padding} x2={chartWidth - padding} y2={chartHeight - padding} stroke="#dee2e6" strokeWidth="1.5" />

                {/* Dynamic Theme Colors mapping directly to Bootstrap Primary (#0d6efd) and Danger (#dc3545) matches */}
                <polyline fill="none" stroke="#0d6efd" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points={salesPath} />
                <polyline fill="none" stroke="#dc3545" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points={purchasePath} />

                {/* Data points mapping */}
                {days.map((day, idx) => (
                    <g key={idx}>
                        <circle cx={getX(idx)} cy={getY(salesData[idx])} r="4" fill="#0d6efd" stroke="#ffffff" strokeWidth="1.5" />
                        <circle cx={getX(idx)} cy={getY(purchaseData[idx])} r="4" fill="#dc3545" stroke="#ffffff" strokeWidth="1.5" />
                        <text x={getX(idx)} y={chartHeight - padding + 18} textAnchor="middle" fill="#6c757d" fontSize="10" style={{ fontFamily: 'sans-serif' }}>
                            {day}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
}
